import {Button, Checkbox, Input, InputNumber, Modal, Segmented, Space, Switch} from 'antd'
import FieldDecorator from 'components/form/FieldDecorator'
import FieldsFactory from 'components/form/FieldsFactory'
import {StepsForm} from 'components/form/StepsForm'
import {createValue} from 'components/form/utils'
import Pure from 'components/Pure'
import {Field, Formik, useFormikContext} from 'formik'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useEffect, useState} from 'react'
import {formatterNumber} from '../../helpers/formatterNumber'
import {parseNumber} from '../../helpers/parseNumber'
import {renderIf} from '../Shared'
import TooltipApp from "components/TooltipApp";
import {BsXLg} from "react-icons/bs";
import {ifThenElseMultipleConditions} from "../../helpers/ifThenElseMultipleConditions";
import PostSettingForm from "../../components/Feed/PostForm/PostSettingForm";
import {ChipGroup, Section} from "../../components/form/ModernForm";

// Translation keys cần thêm:
// 'poll'                      : 'Bình chọn'
// 'quiz'                      : 'Đố vui'
// 'poll / quiz'               : 'Loại bài đăng'
// 'poll / quiz description'   : 'Bình chọn: không có đáp án đúng/sai • Đố vui: cần đánh dấu đáp án đúng'
// 'is yes/no question?'       : 'Loại câu hỏi Đúng / Sai?'
// 'yes/no questions description' : 'Có: 2 lựa chọn Đúng / Sai • Không: đáp án tuỳ chỉnh'
// 'answers can be voted on'   : 'Các đáp án có thể bình chọn'
// 'mark correct answer hint'  : '* Tick vào ô bên trái để đánh dấu đáp án đúng'
// 'correct answer'            : 'Đáp án đúng'
// 'questions/voting content'  : 'Câu hỏi / nội dung bình chọn'
// 'questions/ content'        : 'Nhập câu hỏi hoặc nội dung...'

const Answers = ({onChange, defaultValue = [], isQuiz = false}) => {
    const t = useTranslate()
    // Gán stable _key cho mỗi answer để tránh key={index} gây lệch DOM khi xóa
    const [answers, setAnswers] = useState(() =>
        defaultValue.map((ans, i) => ({
            ...ans,
            _key: ans.id != null ? String(ans.id) : `new_${i}_${Date.now()}`,
        }))
    )
    const [newAnswer, setNewAnswer] = useState('')

    useEffect(() => {
        onChange(answers)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [answers])

    const handleAddNewAnswer = (e) => {
        e.preventDefault()
        if (!newAnswer.trim()) return
        setAnswers([...answers, {id: null, title: newAnswer, is_correct: 0, _key: `new_${Date.now()}`}])
        setNewAnswer('')
    }

    const handleRemoveAnswer = (key) => {
        setAnswers(answers.filter((ans) => ans._key !== key))
    }

    const handleChangeTitle = (key, value) => {
        setAnswers(answers.map((ans) =>
            ans._key === key ? {...ans, title: value} : ans
        ))
    }

    const handleToggleCorrect = (key, checked) => {
        setAnswers(answers.map((ans) => ({
            ...ans,
            is_correct: ans._key === key ? (checked ? 1 : 0) : ans.is_correct ?? 0,
        })))
    }

    return (
        <div className="w-full flex flex-col gap-2">
            <form onSubmit={handleAddNewAnswer}>
                <div className="w-full flex items-center gap-2">
                    <Input
                        key="create new answer"
                        onChange={(e) => setNewAnswer(e.target.value)}
                        placeholder={t('input answer')}
                        value={newAnswer}
                    />
                    <TooltipApp title={t('add answers to poll questions')} rootClassName="text-xs italic">
                        <button
                            style={{ height: 32 }}
                            className="px-4 flex flex-center bg-secondary-600 hover:bg-secondary-700 border border-secondary-500 rounded-md text-white cursor-pointer"
                            onClick={handleAddNewAnswer}>
                            {t('add')}
                        </button>
                    </TooltipApp>
                </div>
            </form>
            {answers.map((answer) => {
                const isCorrect = !!answer.is_correct
                return (
                    <div
                        key={answer._key}
                        className="flex items-center gap-3 rounded-md animated fadeInUp faster"
                        style={{
                            padding: '4px 8px',
                            border: `1px solid ${isQuiz ? (isCorrect ? '#52c41a' : '#d9d9d9') : '#d9d9d9'}`,
                            background: isQuiz && isCorrect ? '#f6ffed' : 'transparent',
                            transition: 'all 0.2s ease',
                        }}>
                        {isQuiz && (
                            <TooltipApp title={t('correct answer')}>
                                <Checkbox
                                    checked={isCorrect}
                                    onChange={(e) => handleToggleCorrect(answer._key, e.target.checked)}
                                    style={{flexShrink: 0}}
                                />
                            </TooltipApp>
                        )}
                        <Input
                            bordered={false}
                            style={{flex: 1, padding: '4px 0'}}
                            // controlled value thay vì defaultValue để React luôn sync đúng với state
                            value={answer.title}
                            onChange={(e) => handleChangeTitle(answer._key, e.target.value)}
                            suffix={
                                <TooltipApp title={t('remove')}>
                                    <BsXLg
                                        size={11}
                                        onClick={() => handleRemoveAnswer(answer._key)}
                                        className="flex text-color-300 hover:text-primary-500 cursor-pointer"
                                        style={{transition: 'color 0.2s'}}
                                    />
                                </TooltipApp>
                            }
                        />
                    </div>
                )
            })}
        </div>
    )
}

const PollForm = ({onBack, onChange}) => {
    const t = useTranslate()
    const {values} = useFormikContext()
    const isQuiz = _.get(values, 'poll_type') === 'quiz'

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
                <div className="mb-4 pb-3 border-b border-color-100 text-center font-bold text-base md:text-lg text-color-000 uppercase tracking-wider">
                    {t('post poll')}
                </div>
                {/* 1. Channel */}
                <PostSettingForm/>
            </div>

            {/* 2. Note — inline simple */}
            <div className="space-x-1 tracking-wider text-xs italic">
                <span className="font-semibold text-gray-600">
                    {`* ${t('note')}: `}
                </span>
                <span className="text-color-200">
                    {t('note create poll description')}
                </span>
            </div>

            {/* 4. Đúng/Sai — flex-col, description dài không bị chèn */}
            <Field
                name="is_yesno_question"
                render={({field}) => (
                    <FieldDecorator
                        name="is_yesno_question"
                        labelProps={{className: undefined}}
                        hasError={false}
                        label={t('is yes/no question?')}
                        description={t('yes/no questions description')}>
                        <ChipGroup
                            options={[
                                {label: t('yes'), value: 1},
                                {label: t('no'), value: 0},
                            ]}
                            value={_.get(values, 'is_yesno_question', 0)}
                            onChange={(value) => field.onChange(createValue('is_yesno_question', value))}
                        />
                        {/*<Segmented*/}
                        {/*    className="w-fit custom-segmented"*/}
                        {/*    defaultValue={_.get(values, 'is_yesno_question', 0)}*/}
                        {/*    options={[*/}
                        {/*        {label: t('yes'), value: 1},*/}
                        {/*        {label: t('no'), value: 0},*/}
                        {/*    ]}*/}
                        {/*    onChange={(value) => field.onChange(createValue('is_yesno_question', value))}*/}
                        {/*/>*/}
                    </FieldDecorator>
                )}
            />
            {/*<Section>*/}
            {/*    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">*/}
            {/*        <Field*/}
            {/*            name="poll_type"*/}
            {/*            render={({field}) => (*/}
            {/*                <FieldDecorator*/}
            {/*                    className="flex flex-col"*/}
            {/*                    name="poll_type"*/}
            {/*                    labelProps={{className: undefined}}*/}
            {/*                    hasError={false}*/}
            {/*                    label={t('poll / quiz')}*/}
            {/*                    description={t('poll / quiz description')}>*/}
            {/*                    <Segmented*/}
            {/*                        className="w-fit custom-segmented"*/}
            {/*                        defaultValue={_.get(values, 'poll_type', 'poll')}*/}
            {/*                        options={[*/}
            {/*                            {label: t('poll'), value: 'poll'},*/}
            {/*                            {label: t('fun quiz'), value: 'quiz'},*/}
            {/*                        ]}*/}
            {/*                        onChange={(value) => field.onChange(createValue('poll_type', value))}*/}
            {/*                    />*/}
            {/*                </FieldDecorator>*/}
            {/*            )}*/}
            {/*        />*/}
            {/*        <Field*/}
            {/*            name="is_yesno_question"*/}
            {/*            render={({field}) => (*/}
            {/*                <FieldDecorator*/}
            {/*                    className="flex flex-col"*/}
            {/*                    name="is_yesno_question"*/}
            {/*                    labelProps={{className: undefined}}*/}
            {/*                    hasError={false}*/}
            {/*                    label={t('is yes/no question?')}*/}
            {/*                    description={t('yes/no questions description')}>*/}
            {/*                    <Segmented*/}
            {/*                        className="w-fit custom-segmented"*/}
            {/*                        defaultValue={_.get(values, 'is_yesno_question', 0)}*/}
            {/*                        options={[*/}
            {/*                            {label: t('yes'), value: 1},*/}
            {/*                            {label: t('no'), value: 0},*/}
            {/*                        ]}*/}
            {/*                        onChange={(value) => field.onChange(createValue('is_yesno_question', value))}*/}
            {/*                    />*/}
            {/*                </FieldDecorator>*/}
            {/*            )}*/}
            {/*        />*/}
            {/*    </div>*/}
            {/*</Section>*/}

            {/* 3. Câu hỏi — full width */}
            <Section>
                <Pure>
                    <FieldsFactory
                        formSchema={[
                            {
                                children: {
                                    type: 'title',
                                    name: 'title',
                                    label: t('questions/voting content'),
                                    component: Input,
                                    withProps: {
                                        size: 'large',
                                        type: 'text',
                                        placeholder: t('questions/ content'),
                                    },
                                },
                            },
                        ]}
                    />
                </Pure>
                {/* 6. Đáp án — nhóm riêng, có visual border */}
                {renderIf(
                    !!!_.get(values, 'is_yesno_question', 0),
                    <div className="flex flex-col gap-2">
                        <Field
                            name="answers"
                            render={({field, form: {values = {}, touched = {}, errors = {}} = {
                                     touched: {},
                                     errors: {},
                                 },}) => (
                                <FieldDecorator
                                    errors={errors.answers}
                                    labelProps={{className: undefined}}
                                    label={t('answers can be voted on')}
                                    description={isQuiz ? t('mark correct answer hint') : undefined}>
                                    <Answers
                                        onChange={(value) => {
                                            field.onChange(createValue('answers', value))
                                        }}
                                        defaultValue={_.get(values, 'answers', [])}
                                        isQuiz={isQuiz}
                                    />
                                </FieldDecorator>
                            )}
                        />
                        <Field
                            name="customizable_answers"
                            render={({field}) => (
                                <div className="mt-2 flex flex-col">
                                    <Checkbox
                                        className="text-sm text-color-200"
                                        disabled={!!_.get(values, 'is_yesno_question', 0) || (_.get(values, 'poll_type', 'poll') === 'quiz')}
                                        checked={Number(field.value)}
                                        onChange={(e) =>
                                            field.onChange(
                                                createValue(
                                                    'customizable_answers',
                                                    e.target.checked ? '1' : '0'
                                                )
                                            )
                                        }>
                                        {t('allow add new answers')}
                                    </Checkbox>
                                </div>
                            )}
                        />
                    </div>
                )}
            </Section>


            {/* 5. Cách bình chọn — InputNumber inline ngay cạnh khi cần */}
            <div className="p-3 border-2 border-slate-300 rounded-xl flex flex-col md:flex-row md:items-end gap-3">
                <div className="flex flex-col flex-1">
                    <span className="font-semibold text-sm text-slate-600 tracking-wide">
                        {t('number of possible answers in a poll')}
                    </span>
                    <span className="text-xs text-slate-400 leading-snug tracking-wider">
                        {t('answer selection limit')}
                    </span>
                </div>
                <Field
                    name="is_single_selection"
                    render={({field}) => (
                        <FieldDecorator
                            name="is_single_selection"
                            labelProps={{className: undefined}}
                            hasError={false}>
                            <div className="flex items-center gap-3">
                                <Segmented
                                    className="w-fit custom-segmented"
                                    disabled={!!_.get(values, 'is_yesno_question', 0) || (_.get(values, 'poll_type', 'poll') === 'quiz')}
                                    defaultValue={_.get(values, 'is_single_selection', 1)}
                                    value={
                                        ifThenElseMultipleConditions(
                                            _.get(values, 'is_single_selection', 1),
                                            !!_.get(values, 'is_yesno_question', 0), 1,
                                            (_.get(values, 'poll_type', 'poll') === 'quiz'), 1
                                        )
                                    }
                                    options={[
                                        {label: t('one answer'), value: 1},
                                        {label: t('multiple answer'), value: 0},
                                    ]}
                                    onChange={(value) =>
                                        field.onChange(createValue('is_single_selection', value))
                                    }
                                />
                                {!!!_.get(values, 'is_yesno_question', 0) && !!!_.get(values, 'is_single_selection') && (
                                    <Field
                                        name="vote_on_maximum"
                                        render={({field: f, form}) => (
                                            <InputNumber
                                                min={1}
                                                max={_.get(form, 'values.answers.length', 2)}
                                                name="vote_on_maximum"
                                                inputMode="numeric"
                                                style={{width: 60}}
                                                formatter={formatterNumber}
                                                parser={parseNumber}
                                                defaultValue={1}
                                                value={
                                                    !!_.get(values, 'is_yesno_question', 0)
                                                        ? 1
                                                        : _.get(values, 'vote_on_maximum', 1)
                                                }
                                                onChange={(value) =>
                                                    f.onChange(createValue('vote_on_maximum', value))
                                                }
                                            />
                                        )}
                                    />
                                )}
                            </div>
                        </FieldDecorator>
                    )}
                />
            </div>

        </div>
    )
}

const CreatePollRoutes = [
    {
        path: '/questtionAndAnswers',
        name: 'create a poll',
        component: PollForm,
        exact: true,
    },
]

const tranformBeforeSubmit = (values = {}) => ({
    ...values,
    vote_on_maximum:
        !!values.is_single_selection || !!values.is_yesno_question
            ? 1
            : values.vote_on_maximum,
    answers: JSON.stringify(
        _.get(values, 'answers', []).map((obj) => ({
            id: obj.id ?? null,
            title: obj.title,
            is_correct: obj.is_correct ?? 0,
        }))
    ),
})

export default function CreatePoll({
                                       initialValues = {
                                           title: '',
                                           poll_type: 'poll',
                                           vote_on_maximum: 1,
                                           customizable_answers: 0,
                                           is_single_selection: 1,
                                           is_yesno_question: 0,
                                           answers: [],
                                       },
                                       onSubmit,
                                       onCancel,
                                       isLoading,
                                   }) {
    const t = useTranslate()
    const handleSubmit = (values) => {
        onSubmit(tranformBeforeSubmit(values))
    }
    const validate = (values) => {
        const {title, is_yesno_question} = values ?? {}
        const answers = Array.from(values?.answers ?? [])
        let errors = {}
        if (_.isEmpty(String(title ?? '').trim())) {
            errors.title = t('required field')
        }
        if (!is_yesno_question && !answers?.length) {
            errors.answers = t('required field')
        }
        return errors
    }

    return (
        <Formik
            initialValues={initialValues}
            validate={validate}
            onSubmit={handleSubmit}>
            {({submitForm}) => (
                <>
                    <PollForm />
                    <div className="flex flex-col gap-2 mt-4 pt-4 border-t border-color-100">
                        <Button
                            color="primary" variant="solid" size="large"
                            loading={isLoading}
                            onClick={submitForm}>
                            {t('publish a post')}
                        </Button>
                        <Button onClick={onCancel} color="default" variant="link"
                                className="text-color-300">
                            {t('close')}
                        </Button>
                    </div>
                </>
            )}
        </Formik>
    )

    // return (
    //     <StepsForm
    //         className="pb-4"
    //         validate={validate}
    //         isLoading={isLoading}
    //         onSubmit={handleSubmit}
    //         routes={CreatePollRoutes}
    //         initialValues={initialValues}
    //         onCancel={onCancel}
    //     />
    // )
}